import { StateCreator } from 'zustand';
import {
  applyFetchedTypingForConversation,
  mergeTypingIndicatorsForConversation,
  shouldUpdateTypingIndicator,
} from '../utils';
import { TypingIndicatorSliceState, TypingIndicatorSlice } from './index.types';

const initialState: TypingIndicatorSliceState = {
  typingIndicators: new Map(),
};

export const createTypingIndicatorSlice: StateCreator<
  TypingIndicatorSlice & Partial<TypingIndicatorSlice>
> = (set, get) => ({
  ...initialState,
  updateTypingIndicatorForPersonInConversation: (
    conversationId,
    personId,
    typingIndicator
  ) => {
    const currentTypingIndicatorForPerson = get()
      .typingIndicators.get(conversationId)
      ?.get(personId);

    if (
      !shouldUpdateTypingIndicator(
        currentTypingIndicatorForPerson,
        typingIndicator.lastUpdatedAt
      )
    ) {
      return false;
    }

    set((state) => ({
      typingIndicators: new Map(state.typingIndicators).set(
        conversationId,
        mergeTypingIndicatorsForConversation(
          state.typingIndicators.get(conversationId),
          new Map([[personId, typingIndicator]])
        )
      ),
    }));

    return true;
  },
  removeTypingIndicatorForPersonInConversation: (
    conversationId,
    personId,
    lastUpdatedAt
  ) => {
    set((state) => {
      const currentForConversation = state.typingIndicators.get(conversationId);
      const currentForPerson = currentForConversation?.get(personId);

      if (
        !currentForConversation ||
        !currentForPerson ||
        currentForPerson.lastUpdatedAt !== lastUpdatedAt
      ) {
        return state;
      }

      const nextForConversation = new Map(currentForConversation);
      nextForConversation.delete(personId);

      const nextTypingIndicators = new Map(state.typingIndicators);

      if (nextForConversation.size === 0) {
        nextTypingIndicators.delete(conversationId);
      } else {
        nextTypingIndicators.set(conversationId, nextForConversation);
      }

      return { typingIndicators: nextTypingIndicators };
    });
  },
  clearTypingIndicators: () => {
    set({ typingIndicators: new Map() });
  },
  getTypingPersonsInConversation: (conversationId: string) => {
    const indicators = get().typingIndicators.get(conversationId);

    if (!indicators) {
      return [];
    }

    return Array.from(indicators.entries())
      .filter(([, indicator]) => indicator.state === 'typing')
      .sort(
        ([, left], [, right]) => left.startedTypingAt - right.startedTypingAt
      )
      .map(([personId]) => personId);
  },
  applyFetchedTypingForConversations: (conversations) => {
    const conversationsWithTyping = conversations.filter(
      (conversation) => conversation.id && conversation.typing
    );

    if (conversationsWithTyping.length === 0) {
      return;
    }

    set((state) => {
      const nextTypingIndicators = new Map(state.typingIndicators);

      for (const conversation of conversationsWithTyping) {
        const conversationId = conversation.id;
        const nextForConversation = applyFetchedTypingForConversation(
          nextTypingIndicators.get(conversationId),
          conversation.typing ?? []
        );

        if (nextForConversation.size === 0) {
          nextTypingIndicators.delete(conversationId);
        } else {
          nextTypingIndicators.set(conversationId, nextForConversation);
        }
      }

      return { typingIndicators: nextTypingIndicators };
    });
  },
});
